import { useState, useEffect } from 'preact/hooks';
import { superAdminFetch } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingNotifications, setTestingNotifications] = useState(false);
  const [notificationResult, setNotificationResult] = useState(null);
  const [testingWelcome, setTestingWelcome] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const [statsRes, workspacesRes] = await Promise.all([
        superAdminFetch(`${API_URL}/api/super-admin/stats`),
        superAdminFetch(`${API_URL}/api/super-admin/workspaces?limit=10`)
      ]);

      if (!statsRes.ok || !workspacesRes.ok) {
        const errorData = await statsRes.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch data');
      }

      const statsData = await statsRes.json();
      const workspacesData = await workspacesRes.json();

      setStats(statsData);
      setWorkspaces(workspacesData.workspaces || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading super admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Access Denied</h2>
          <p>{error}</p>
          <p>You must be authenticated via Cloudflare Access with a super admin email.</p>
        </div>
      </div>
    );
  }

  function handleLogout() {
    // Cloudflare Access logout URL
    window.location.href = 'https://maltashopper.cloudflareaccess.com/cdn-cgi/access/logout';
  }

  async function testNotifications() {
    setTestingNotifications(true);
    setNotificationResult(null);
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/test-webhook`, {
        method: 'POST'
      });
      const data = await res.json();
      setNotificationResult(data);
    } catch (err) {
      setNotificationResult({ success: false, message: err.message });
    } finally {
      setTestingNotifications(false);
    }
  }

  async function testWelcomeEmail() {
    setTestingWelcome(true);
    setNotificationResult(null);
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/test-welcome-email`, {
        method: 'POST'
      });
      const data = await res.json();
      setNotificationResult(data);
    } catch (err) {
      setNotificationResult({ success: false, message: err.message });
    } finally {
      setTestingWelcome(false);
    }
  }

  return (
    <div class="super-admin-page">
      <div class="super-admin-header">
        <div class="page-header">
          <h1>Super Admin Dashboard</h1>
          <p class="text-muted">Manage all workspaces and subscriptions</p>
        </div>
        <button onClick={handleLogout} class="btn btn-secondary btn-logout">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>

      {/* Stats Grid */}
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon bg-blue">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
          </div>
          <div class="stat-content">
            <span class="stat-value">{stats?.total_workspaces || 0}</span>
            <span class="stat-label">Total Workspaces</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div class="stat-content">
            <span class="stat-value">{stats?.active_subscriptions || 0}</span>
            <span class="stat-label">Active Subscriptions</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-yellow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div class="stat-content">
            <span class="stat-value">{stats?.trialing || 0}</span>
            <span class="stat-label">In Trial</span>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon bg-red">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <div class="stat-content">
            <span class="stat-value">{stats?.expired || 0}</span>
            <span class="stat-label">Expired</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Quick Actions</h2>
        </div>
        <div class="card-body">
          <div class="button-group">
            <a href="/super-admin/workspaces" class="btn btn-primary">
              View All Workspaces
            </a>
            <a href="/super-admin/plans" class="btn btn-secondary">
              Plans & Pricing
            </a>
            <a href="/super-admin/features" class="btn btn-secondary">
              Manage Features
            </a>
            <a href="/super-admin/settings" class="btn btn-secondary">
              Settings (Xero)
            </a>
            <button
              onClick={testNotifications}
              disabled={testingNotifications}
              class="btn btn-secondary"
            >
              {testingNotifications ? 'Sending...' : 'Test Notifications'}
            </button>
            <button
              onClick={testWelcomeEmail}
              disabled={testingWelcome}
              class="btn btn-secondary"
            >
              {testingWelcome ? 'Sending...' : 'Test Welcome Email'}
            </button>
          </div>
          {notificationResult && (
            <div class={`alert mt-3 ${notificationResult.success ? 'alert-success' : 'alert-danger'}`}>
              <strong>{notificationResult.success ? 'Success!' : 'Error:'}</strong> {notificationResult.message}
              {notificationResult.success && (
                <div class="mt-2 text-sm">
                  Slack: {notificationResult.slack_sent ? '✓' : '✗'} |
                  Email: {notificationResult.email_sent ? '✓' : '✗'} ({notificationResult.email_recipient})
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recent Workspaces */}
      <div class="card mt-4">
        <div class="card-header">
          <h2>Recent Workspaces</h2>
          <a href="/super-admin/workspaces" class="btn btn-sm btn-secondary">View All</a>
        </div>
        <div class="card-body">
          <table class="table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Users</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map(ws => (
                <tr key={ws.id}>
                  <td>
                    <div class="workspace-info">
                      {ws.slack_team_icon && (
                        <img src={ws.slack_team_icon} alt="" class="workspace-icon" />
                      )}
                      <div>
                        <strong>{ws.name}</strong>
                        {ws.domain && <span class="text-muted"> ({ws.domain})</span>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class={`badge badge-${getStatusColor(ws.subscription?.status)}`}>
                      {ws.subscription?.status || 'none'}
                    </span>
                  </td>
                  <td>{ws.subscription?.plan || 'none'}</td>
                  <td>{ws.user_count || 0}</td>
                  <td>{formatDate(ws.created_at)}</td>
                  <td>
                    <a href={`/super-admin/workspaces/${ws.id}`} class="btn btn-sm btn-secondary">
                      Manage
                    </a>
                  </td>
                </tr>
              ))}
              {workspaces.length === 0 && (
                <tr>
                  <td colspan="6" class="text-center text-muted">No workspaces found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'warning';
    case 'expired':
    case 'cancelled': return 'danger';
    case 'suspended': return 'danger';
    default: return 'secondary';
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
